"use client";

import { useT } from "@/lib/i18n/context";
import type { WLatestBlogPostsContent } from "@/lib/api/cms-window-content";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { wLatestBlogPostsTranslations } from "./WLatestBlogPosts.i18n";
import styles from "./WLatestBlogPosts.module.css";

/**
 * WLatestBlogPosts — sekcja "ostatnie wpisy blogowe".
 *
 * Układ mozaikowy dla 3 artykułów:
 *   - Lewa kolumna: featured (items[0]) — zdjęcie (góra) + treść (dół)
 *   - Prawa kolumna: items[1] i items[2] jako poziome karty (zdjęcie + tekst)
 *
 * Responsywne obrazy: `image_phone` przy ≤480px, `image_tablet` przy ≤1024px,
 * `image` jako desktop fallback.
 */
export function WLatestBlogPosts({ content, title }: WLatestBlogPostsProps) {
    const t = useT(wLatestBlogPostsTranslations);

    if (!content || content.items.length === 0) return null;

    const [featured, ...sideItems] = content.items;

    return (
        <section className={styles.root}>
            <div className={styles.inner}>
                <div className={styles.header}>
                    <h2 className={styles.heading}>{title}</h2>
                    {content.href && (
                        <a href={content.href} className={styles.showAllBtn}>
                            {t.showAll}
                        </a>
                    )}
                </div>

                <div className={styles.grid}>
                    <div className={styles.left}>
                        <a href={featured.href} aria-label={featured.name} className={styles.leftImageWrap}>
                            {featured.image ? (
                                <picture>
                                    {featured.image_phone && (
                                        <source srcSet={featured.image_phone} media="(max-width: 480px)" />
                                    )}
                                    {featured.image_tablet && (
                                        <source srcSet={featured.image_tablet} media="(max-width: 1024px)" />
                                    )}
                                    <img src={featured.image} alt={featured.name} className={styles.image} />
                                </picture>
                            ) : (
                                <ImagePlaceholder />
                            )}
                        </a>
                        <div className={styles.body}>
                            <h3 className={styles.cardTitle}>
                                <a href={featured.href} className={styles.titleLink}>
                                    {featured.name}
                                </a>
                            </h3>
                            <div
                                className={styles.excerpt}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: featured.short_text }}
                            />
                            <a href={featured.href} className={styles.readMore}>
                                {t.readMore} <span aria-hidden="true">&#8250;</span>
                            </a>
                        </div>
                    </div>

                    <div className={styles.right}>
                        {sideItems.slice(0, 2).map((item) => (
                            <article key={item.id} className={styles.sideCard}>
                                <a href={item.href} className={styles.sideImageWrap} tabIndex={-1} aria-hidden="true">
                                    {item.image ? (
                                        <picture>
                                            {item.image_phone && (
                                                <source srcSet={item.image_phone} media="(max-width: 480px)" />
                                            )}
                                            {item.image_tablet && (
                                                <source srcSet={item.image_tablet} media="(max-width: 1024px)" />
                                            )}
                                            <img src={item.image} alt="" className={styles.sideImage} />
                                        </picture>
                                    ) : (
                                        <ImagePlaceholder />
                                    )}
                                </a>
                                <div className={styles.body}>
                                    <h3 className={styles.cardTitle}>
                                        <a href={item.href} className={styles.titleLink}>
                                            {item.name}
                                        </a>
                                    </h3>
                                    <div
                                        className={styles.excerpt}
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{ __html: item.short_text }}
                                    />
                                    <a href={item.href} className={styles.readMore}>
                                        {t.readMore} <span aria-hidden="true">&#8250;</span>
                                    </a>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export type WLatestBlogPostsProps = {
    content: WLatestBlogPostsContent | null;
    title: string;
};
